// Master KL Approval
// Schvalování probíhá na úrovni master_certificate (per-instrument-intake), ne per-probe.
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useStats } from '@/contexts/StatsContext';
import { toastApiError } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CheckCircle, XCircle, Loader2, ChevronDown, ChevronUp, FileCode, FileDown, Lock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { isRecent, formatRelative } from '@/lib/uxHelpers';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import ChipFilter from '@/components/ChipFilter';
import RefLink from '@/components/RefLink';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

export default function ApprovalPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Permission helpery — master cert vyžaduje action v ALESPOŇ JEDNÉ ze zahrnutých laboratoří.
  const canDoInAnyLab = (action, labs) => {
    if (!user) return false;
    if (user.isAdmin) return true;
    return (labs || []).some(lab => {
      const perm = (user.permissions || []).find(p => p.module === lab);
      return perm?.actions?.includes(action) || false;
    });
  };
  const userHasAnyTechnicalReview = (user?.isAdmin) ||
    (user?.permissions || []).some(p => (p.actions || []).includes('technical_review'));
  const [activeTab, setActiveTab] = useState(userHasAnyTechnicalReview ? 'technical' : 'final');
  const [pendingMaster, setPendingMaster] = useState([]);  // K technické kontrole
  const [pendingFinal, setPendingFinal] = useState([]);  // K finálnímu schválení
  const [pendingCompletion, setPendingCompletion] = useState([]);  // Čeká na dokončení sond
  const [filterText, setFilterText] = useState('');
  const [filterLabs, setFilterLabs] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [details, setDetails] = useState({});  // {certId: {calibrations: [...]}}
  const [completeness, setCompleteness] = useState({});  // {certId: {complete, errors}}
  const [showReject, setShowReject] = useState(false);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showTechReview, setShowTechReview] = useState(false);
  const [techReviewId, setTechReviewId] = useState(null);
  const [techReviewNotes, setTechReviewNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const { refetch: refetchStats } = useStats();

  const load = () => {
    api.get('/master-certificates?status=pending_approval').then(r => setPendingMaster(r.data)).catch(() => {});
    api.get('/master-certificates?status=under_technical_review').then(r => setPendingFinal(r.data)).catch(() => {});
    // Master KL, které ještě čekají na dokončení všech sond přístroje — nejsou
    // ke schválení, ale zobrazíme je jako upozornění, ať uživatel ví proč.
    api.get('/master-certificates?status=pending_completion').then(r => setPendingCompletion(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Úplnost pro certy k finálnímu schválení — ať uživatel předem vidí,
  // proč (ne)lze schválit, a tlačítko lze zablokovat bez klikání naslepo.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        (pendingFinal || []).map(c =>
          api.get(`/master-certificates/${c.id}/completeness`)
            .then(r => [c.id, r.data])
            .catch(() => [c.id, null])
        )
      );
      if (cancelled) return;
      setCompleteness(prev => {
        const next = { ...prev };
        for (const [id, data] of results) if (data) next[id] = data;
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [pendingFinal]);

  const toggleExpand = async (cert) => {
    const id = cert.id;
    const isOpen = expanded[id];
    setExpanded(prev => ({ ...prev, [id]: !isOpen }));
    // Lazy fetch detail (calibrations) when first expanding.
    if (!isOpen && !details[id]) {
      try {
        const res = await api.get(`/master-certificates/${id}`);
        setDetails(prev => ({ ...prev, [id]: res.data }));
      } catch (e) { /* ignore */ }
    }
  };

  const downloadCertPdf = (certId, certNumber) => {
    const token = localStorage.getItem('ct-token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const url = `${baseUrl}/api/master-certificates/${certId}/certificate.pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) return r.json().then(d => { throw new Error(d.detail || t('approvals.error-pdf-cannot-download')); });
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${certNumber || 'KL'}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(err => toastApiError(err, t('approvals.error-pdf-unavailable')));
  };

  const downloadDccXml = (certId, certNumber) => {
    const token = localStorage.getItem('ct-token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const url = `${baseUrl}/api/master-certificates/${certId}/dcc-xml`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error(t('approvals.error-dcc-unavailable'));
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${certNumber || 'DCC'}.xml`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(err => toastApiError(err, t('approvals.error-dcc-unavailable')));
  };

  const approve = async (id) => {
    setLoading(true);
    try {
      const res = await api.put(`/master-certificates/${id}/approve`);
      const certNum = res.data?.certificateNumber;
      const hasDcc = res.data?.hasDccXml;
      load();
      refetchStats();
      if (hasDcc) {
        toast.success(
          <div className="space-y-1">
            <div>{t('approvals.toast-approved-title', { certNumber: certNum })}</div>
            <div className="flex items-center gap-3 mt-1">
              <button
                onClick={() => downloadCertPdf(id, certNum)}
                className="flex items-center gap-1 text-xs underline"
                style={{ color: 'var(--ct-accent)' }}
              >
                <FileDown className="w-3 h-3" /> {t('approvals.btn-download-pdf')}
              </button>
              <button
                onClick={() => downloadDccXml(id, certNum)}
                className="flex items-center gap-1 text-xs underline"
                style={{ color: 'var(--ct-accent)' }}
              >
                <FileCode className="w-3 h-3" /> {t('approvals.btn-download-dcc')}
              </button>
            </div>
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.warning(
          <div className="space-y-1">
            <div className="font-semibold">{t('approvals.toast-approved-no-dcc-title', { certNumber: certNum })}</div>
            <div className="text-xs">{t('approvals.toast-approved-no-dcc-desc')}</div>
          </div>,
          { duration: 8000 }
        );
      }
    } catch (err) {
      toastApiError(err, t('approvals.error-approve'));
    } finally {
      setLoading(false);
    }
  };

  const reject = async () => {
    if (!rejectId) return;
    setLoading(true);
    try {
      await api.put(`/master-certificates/${rejectId}/reject`, { reason: rejectReason });
      load();
      refetchStats();
      setShowReject(false);
      setRejectReason('');
      setRejectId(null);
      toast.success(t('approvals.toast-rejected'), {
        action: { label: t('approvals.toast-rejected-action'), onClick: () => window.location.assign('/calibrations') },
      });
    } catch (err) { toastApiError(err); }
    finally { setLoading(false); }
  };

  const technicalReview = async () => {
    if (!techReviewId) return;
    setLoading(true);
    try {
      await api.put(`/master-certificates/${techReviewId}/technical-review`, { notes: techReviewNotes });
      load();
      refetchStats();
      setShowTechReview(false);
      setTechReviewNotes('');
      setTechReviewId(null);
      toast.success(t('approvals.toast-tech-reviewed'));
    } catch (err) { toastApiError(err, t('approvals.error-tech-review')); }
    finally { setLoading(false); }
  };

  const activeList = activeTab === 'technical' ? pendingMaster : pendingFinal;
  const labsInList = Array.from(new Set(activeList.flatMap(c => c.laboratories || []).filter(Boolean)));
  const filteredCerts = activeList.filter(c => {
    if (filterLabs.length > 0 && !(c.laboratories || []).some(l => filterLabs.includes(l))) return false;
    if (filterText.trim()) {
      const q = filterText.toLowerCase();
      const hay = [c.instrumentName, c.instrumentType, c.customerName, c.orderNumber,
        c.intakeProtocolNumber, c.serialNumber].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <div data-testid="approval-page" className="space-y-4">
      <div>
        <h1 className="ct-h1">{t('approvals.title')}</h1>
        <p className="text-sm" style={{ color: 'var(--ct-text-secondary)' }}>
          {t('approvals.subtitle')}
        </p>
      </div>

      {pendingCompletion.length > 0 && (
        <div
          data-testid="pending-completion-warning"
          className="rounded-md px-4 py-3 text-sm space-y-1"
          style={{ background: 'var(--ct-warning-soft, rgba(234,179,8,0.12))', border: '1px solid var(--ct-warning, #eab308)' }}
        >
          <div className="flex items-center gap-2 font-medium" style={{ color: 'var(--ct-warning, #b45309)' }}>
            <AlertTriangle className="w-4 h-4" />
            Čeká na dokončení sond ({pendingCompletion.length}) — tyto přístroje ještě nejsou ke schválení
          </div>
          <ul className="text-xs space-y-0.5 pl-6" style={{ color: 'var(--ct-text-secondary)' }}>
            {pendingCompletion.map(c => (
              <li key={c.id}>
                {c.instrumentName || 'Přístroj'} {c.serialNumber ? `(${c.serialNumber})` : ''}
                {' — '}dokončeno {c.completedCount || 0}/{c.calibrationCount || 0} sond
                {c.intakeProtocolNumber ? `, příjem ${c.intakeProtocolNumber}` : ''}
              </li>
            ))}
          </ul>
          <div className="text-xs pl-6" style={{ color: 'var(--ct-text-secondary)' }}>
            Dokončete zbývající sondy v modulu Kalibrace — master KL se pak sám objeví ke schválení.
          </div>
        </div>
      )}

      <div className="flex gap-1 border-b" style={{ borderColor: 'var(--ct-border)' }}>
        <button
          onClick={() => setActiveTab('technical')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'technical' ? '' : 'opacity-60 hover:opacity-100'
          }`}
          style={{
            borderColor: activeTab === 'technical' ? 'var(--ct-accent)' : 'transparent',
            color: activeTab === 'technical' ? 'var(--ct-accent)' : 'var(--ct-text-secondary)',
          }}
          data-testid="tab-technical-review"
        >
          {t('approvals.tab-technical')} ({pendingMaster.length})
        </button>
        <button
          onClick={() => setActiveTab('final')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'final' ? '' : 'opacity-60 hover:opacity-100'
          }`}
          style={{
            borderColor: activeTab === 'final' ? 'var(--ct-accent)' : 'transparent',
            color: activeTab === 'final' ? 'var(--ct-accent)' : 'var(--ct-text-secondary)',
          }}
          data-testid="tab-final-approval"
        >
          {t('approvals.tab-final')} ({pendingFinal.length})
        </button>
      </div>

      <p className="text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
        {activeTab === 'technical' ? t('approvals.step-6-1-help') : t('approvals.step-6-2-help')}
        {' '}{t('approvals.filtered', { filtered: filteredCerts.length, total: activeList.length })}
      </p>

      <div className="space-y-2">
        <Input
          placeholder={t('approvals.search-placeholder')}
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          className="h-9 text-sm"
        />
        {labsInList.length > 1 && (
          <ChipFilter
            label={t('approvals.lab-filter-label')}
            options={labsInList.map(l => ({ id: l, label: l }))}
            value={filterLabs}
            onChange={setFilterLabs}
          />
        )}
      </div>

      <ScrollArea className="max-h-[calc(100vh-12rem)]">
        <div className="space-y-3">
          {filteredCerts.map(cert => {
            const isExpanded = expanded[cert.id];
            const labs = cert.laboratories || [];
            const canTech = canDoInAnyLab('technical_review', labs);
            const canApprove = canDoInAnyLab('approve', labs);
            const detail = details[cert.id];
            const cals = detail?.calibrations || [];

            return (
              <div key={cert.id}
                className={`ct-panel ${cert.createdAt && isRecent(cert.createdAt) ? 'ct-row-new' : ''}`}>
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer"
                  onClick={() => toggleExpand(cert)}
                  style={{ borderBottom: isExpanded ? '1px solid var(--ct-border)' : 'none' }}
                >
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    {isExpanded
                      ? <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--ct-text-secondary)' }} />
                      : <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--ct-text-secondary)' }} />}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap text-xs mb-1" onClick={(e) => e.stopPropagation()}>
                        {cert.customerName && (
                          <span className="font-semibold" style={{ color: 'var(--ct-accent)' }}>{cert.customerName}</span>
                        )}
                        {cert.orderNumber && <RefLink type="order" id={cert.orderNumber} />}
                        {cert.intakeProtocolNumber && <RefLink type="intake" id={cert.intakeProtocolNumber} />}
                        {cert.submittedForApprovalAt && (
                          <span style={{ color: 'var(--ct-text-secondary)' }}
                            title={new Date(cert.submittedForApprovalAt).toLocaleString(t('common.date-locale'))}>
                            {t('approvals.submitted-prefix')} {formatRelative(cert.submittedForApprovalAt)}
                          </span>
                        )}
                        {activeTab === 'final' && cert.technicalReviewedAt && (
                          <span style={{ color: 'var(--ct-success)' }}
                            title={new Date(cert.technicalReviewedAt).toLocaleString(t('common.date-locale'))}>
                            {t('approvals.tech-ok-prefix')} {formatRelative(cert.technicalReviewedAt)}
                            {cert.technicalReviewedByName ? ` (${cert.technicalReviewedByName})` : ''}
                          </span>
                        )}
                      </div>
                      <div className="font-medium">{cert.instrumentName}</div>
                      <div className="text-xs mt-0.5" style={{ color: 'var(--ct-text-secondary)' }}>
                        {cert.instrumentType} | {t('approvals.serial-prefix')} {cert.serialNumber || '—'} | {t('approvals.probes-count', { count: cert.calibrationCount })} | {labs.join(', ') || '—'}
                      </div>
                      {activeTab === 'final' && cert.technicalReviewNotes && (
                        <div className="text-xs italic mt-1 px-2 py-1 rounded"
                          style={{ color: 'var(--ct-text-secondary)', background: 'var(--ct-bg-secondary)' }}
                          onClick={(e) => e.stopPropagation()}>
                          {t('approvals.metrologist-note-prefix')} „{cert.technicalReviewNotes}"
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {activeTab === 'technical' ? (
                      <>
                        {!canTech && (
                          <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
                            <Lock className="w-3 h-3" /> {t('approvals.no-permission')}
                          </span>
                        )}
                        <button
                          data-testid={`tech-review-cert-${cert.id}`}
                          onClick={(e) => { e.stopPropagation(); setTechReviewId(cert.id); setShowTechReview(true); }}
                          disabled={!canTech}
                          className="p-2 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          style={{ color: 'var(--ct-success)' }}
                          title={canTech ? t('approvals.tech-approve-title') : t('approvals.tech-approve-no-permission')}
                        >
                          <CheckCircle className="w-5 h-5" />
                        </button>
                        <button
                          data-testid={`reject-cert-${cert.id}`}
                          onClick={(e) => { e.stopPropagation(); setRejectId(cert.id); setShowReject(true); }}
                          disabled={!canTech && !canApprove}
                          className="p-2 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          style={{ color: 'var(--ct-error)' }}
                          title={t('approvals.reject-title')}
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      </>
                    ) : (
                      <>
                        {!canApprove && (
                          <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
                            <Lock className="w-3 h-3" /> {t('approvals.no-permission')}
                          </span>
                        )}
                        {completeness[cert.id]?.complete === false && (
                          <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--ct-warning)' }}
                            title={completeness[cert.id].errors.join('\n')}>
                            <AlertTriangle className="w-3 h-3" /> {t('approvals.incomplete-badge')}
                          </span>
                        )}
                        <button
                          data-testid={`approve-cert-${cert.id}`}
                          onClick={(e) => { e.stopPropagation(); approve(cert.id); }}
                          disabled={!canApprove || completeness[cert.id]?.complete === false}
                          className="p-2 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          style={{ color: 'var(--ct-success)' }}
                          title={
                            !canApprove ? t('approvals.final-approve-no-permission')
                              : completeness[cert.id]?.complete === false ? t('approvals.incomplete-title')
                                : t('approvals.final-approve-title')
                          }
                        >
                          <CheckCircle className="w-5 h-5" />
                        </button>
                        <button
                          data-testid={`reject-cert-${cert.id}`}
                          onClick={(e) => { e.stopPropagation(); setRejectId(cert.id); setShowReject(true); }}
                          disabled={!canApprove}
                          className="p-2 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          style={{ color: 'var(--ct-error)' }}
                          title={t('approvals.reject-title')}
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Expanded — seznam sond se souhrnem */}
                {isExpanded && (
                  <div className="px-4 py-3 space-y-2" style={{ background: 'var(--ct-bg-secondary)' }}>
                    {activeTab === 'final' && completeness[cert.id]?.complete === false && (
                      <div className="flex items-start gap-2 px-3 py-2 rounded text-xs"
                        style={{ background: 'rgba(234,179,8,0.12)', border: '1px solid var(--ct-warning)', color: 'var(--ct-text)' }}>
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--ct-warning)' }} />
                        <div>
                          <div className="font-medium">{t('approvals.incomplete-heading')}</div>
                          <ul className="mt-1 list-disc list-inside">
                            {completeness[cert.id].errors.map((er, idx) => <li key={idx}>{er}</li>)}
                          </ul>
                        </div>
                      </div>
                    )}
                    <div className="text-xs uppercase tracking-wider font-semibold"
                      style={{ color: 'var(--ct-text-secondary)' }}>
                      {t('approvals.probes-list-title', { count: cals.length })}
                    </div>
                    {cals.length === 0 && <div className="text-xs italic">{t('approvals.loading')}</div>}
                    {cals.map((cal, i) => {
                      const points = (cal.measurements || {}).points || [];
                      const unc = cal.uncertaintyResults || {};
                      return (
                        <div key={cal.id} className="ct-panel-inset px-3 py-2 rounded text-xs"
                          style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)' }}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold">{t('approvals.probe-prefix', { n: i + 1 })} {cal.laboratory}</span>
                            <span style={{ color: 'var(--ct-text-secondary)' }}>
                              {cal.probeName || cal.probeCode || '—'}
                            </span>
                            <StatusBadge status={cal.result} />
                            <span style={{ color: 'var(--ct-text-secondary)' }}>
                              · {t('approvals.points-suffix', { count: points.length })}
                              {unc.expanded != null ? ` · U=${unc.expanded}` : ''}
                            </span>
                          </div>
                          <div className="mt-1" style={{ color: 'var(--ct-text-secondary)' }}>
                            {t('approvals.methodology-label')} {cal.methodologyCode || '—'} · {t('approvals.calibrated-by-label')} {cal.calibratingUserName || '—'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {activeList.length === 0 && (
            <EmptyState
              icon={CheckCircle}
              title={activeTab === 'technical' ? t('approvals.empty-technical') : t('approvals.empty-final')}
              description={activeTab === 'technical' ? t('approvals.empty-technical-desc') : t('approvals.empty-final-desc')}
            />
          )}
        </div>
      </ScrollArea>

      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent style={{ background: 'var(--ct-surface)', border: '1px solid var(--ct-border)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--ct-error)' }}>{t('approvals.reject-dialog-title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
              {t('approvals.reject-dialog-desc')}
            </p>
            <label className="ct-form-label">{t('approvals.reject-reason-label')}</label>
            <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
              placeholder={t('approvals.reject-reason-placeholder')} rows={3} className="text-sm mt-1" />
          </div>
          <DialogFooter>
            <Button onClick={reject} disabled={loading} className="gap-1"
              style={{ background: 'var(--ct-error)', color: '#fff' }}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {t('approvals.reject-title')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showTechReview} onOpenChange={setShowTechReview}>
        <DialogContent style={{ background: 'var(--ct-surface)', border: '1px solid var(--ct-border)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--ct-success)' }}>{t('approvals.tech-review-dialog-title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
              {t('approvals.tech-review-dialog-desc')}
            </p>
            <label className="ct-form-label">{t('approvals.tech-review-notes-label')}</label>
            <Textarea value={techReviewNotes} onChange={e => setTechReviewNotes(e.target.value)}
              placeholder={t('approvals.tech-review-notes-placeholder')} rows={3} className="text-sm mt-1" />
          </div>
          <DialogFooter>
            <Button onClick={technicalReview} disabled={loading} className="gap-1"
              style={{ background: 'var(--ct-success)', color: '#fff' }}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {t('approvals.btn-tech-approve')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
